import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  ColumnMetaData,
  RealTimeStockRowsData,
  TransformedRealTimeStock,
  StatusFilterMenuItems,
} from "../../../types/types";
import { Loader } from "../../../components/loader/Loader";
import {
  realTimeStockColumnsMetadata,
  getMergedCatalogueAndRealTimeStockData,
  getStockSummaryTileData,
  StockSummaryData,
  defaultStockSummaryTileData,
  setRealTimeStockDataInDb,
  getDistributorList,
} from "./RealTimeStockUtils";
import { setBatchWiseStockDataInDb } from "../batchAndExpiryManagement/BatchAndExpiryManagementUtils";
import Header from "../../../components/dms_common/Header";
import RealTimeStockTable from "./RealTimeStockTable";
import {
  filterData,
  defaultSearchFunction,
} from "../../../components/dms_common/DmsCommonServices";
import StockSummaryTile from "./StockSummaryTile";
import EmptyPage from "../../claimsNew/components/SelectClaimType";
import realTimeEmptyImage from "../../../assets/image/svg/realTimeEmptyImage.svg";
import { useNavigate, useLocation } from "react-router-dom";
import { ConfirmationPopUp } from "../../../components/popup/confirmationPopUp";
import { downloadAllReports } from "../../../services/manageReportService";
import { Alert, Snackbar } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { getDataFromLocalDb } from "../../../services/dbService";
import { getConfigData } from "../../../services/configurationService";
import "./RealTimeStock.css";
import { Product } from "../../createSecondaryOrder/models";
import ScreenOverlay from "../../../components/dms_common/ScreenOverlay";
import {
  GenericModal,
  getGenericModalProps,
  initialGenericModalProps,
} from "../../viewOrder/ViewOrderUtils";
import CustomGenericModal from "../../../components/dms_common/CustomGenericModal";
import { logAnalyticsEvent, logLoadTrace } from "../../../firebase";
import { getQuantityType } from "../../../components/dms_common/DmsUtils";
import MultiCategoryFilter from "../../../components/dms_common/MultiCategoryFilter";

const RealTimeStock = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [confirmPopupState, setConfirmPopupState] = useState<boolean>(false);
  const [data, setData] = useState<RealTimeStockRowsData[]>([]);
  const [rows, setRows] = useState<RealTimeStockRowsData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchedValue, setSearchedValue] = useState<string>("");
  const [tileData, setTileData] = useState<StockSummaryData>(defaultStockSummaryTileData);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const realTimeStocksReportRef = useRef<string | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<Dayjs>(dayjs());
  const [isError, setIsError] = useState<boolean>(false);
  const [genericModalProps, setGenericModalProps] = useState<GenericModal>(initialGenericModalProps);
  const [resetSearch, setResetSearch] = useState(false);
  const [showDistributorFilter, setShowDistributorFilter] = useState<boolean>(false);
  const [distributors, setDistributors] = useState<StatusFilterMenuItems[]>([]);
  const [selectedDistributor, setSelectedDistributor] = useState<string>("");
  const [noDistributorsForCompanyUser, setNoDistributorsForCompanyUser] = useState<boolean>(false);
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [filterOpen, setFilterOpen] = useState<boolean>(false);
  const [selectedFilterType, setSelectedFilterType] = useState<string>("brand");
   const [filterSearchValue, setFilterSearchValue] = useState<string>("");

  const useQuery = () => {
    return new URLSearchParams(location.search);
  };
  const query = useQuery();
  const filterParam = query.get("filter");
  const stockTypeParam = query.get("stockType");

  const handleSnackbarClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleSearchValueChange = (newValue: string) => {
    setSearchedValue(newValue);
  };

  const filteredData = useMemo(() => {
    let filtered = filterData(data, searchedValue.toLowerCase(), defaultSearchFunction);

    if (selectedBrands.length > 0) {
      filtered = filtered.filter(row => selectedBrands.includes(row.brand));
    }
    
    if (selectedCategories.length > 0) {
      filtered = filtered.filter(row => selectedCategories.includes(row.category));
    }

    if (filterParam === "outOfStock" && stockTypeParam === "availableQty") {
      filtered = filtered.sort((a, b) => {
        const aHasZeroStock = (a.pieceAvailableQty === 0 && a.caseAvailableQty === 0 && a.otherAvailableQty === 0);
        const bHasZeroStock = (b.pieceAvailableQty === 0 && b.caseAvailableQty === 0 && b.otherAvailableQty === 0);
        
        if (aHasZeroStock && !bHasZeroStock) return -1;
        if (!aHasZeroStock && bHasZeroStock) return 1;
        return 0;
      });
    }
    
    return filtered;
  }, [searchedValue, data, filterParam, stockTypeParam, selectedBrands, selectedCategories]);

  useEffect(() => {
    if(searchedValue){
      logAnalyticsEvent( "real_time_stock_filter", {
        "distributor_id": JSON.parse(localStorage.authContext).user.loginId,
        "real_time_stock_searched":searchedValue,
        "result_count":filteredData.length
      });
    }
    setRows(filteredData); //only rows get impacted from search and filter not the data filter
  }, [filteredData]);

  // Handle distributor change
  const handleDistributorChange = (distributorId: string) => {
    setSelectedDistributor(distributorId);
    // Refresh data with the selected distributor and log loading time
    setSelectedBrands([]);
    setSelectedCategories([]);
    logLoadTrace(() => init(distributorId), "real_time_stock_distributor_change_time");
  };

  // Fetch distributors list
  const fetchDistributors = async () => {
    try {
      const distributorsList = await getDistributorList();
      setDistributors(distributorsList);
      
      // Check if there are any distributors for the company/admin user
      if (distributorsList.length > 0) {
        setNoDistributorsForCompanyUser(false);
        const firstDistributorId = distributorsList[0].id;
        // setSelectedDistributor(firstDistributorId);   //can be problem
        
        // Load the first distributor's data and log loading time
        logLoadTrace(() => init(firstDistributorId), "real_time_stock_page_load_time");
      } else {
        // No distributors available for the company/admin user
        setNoDistributorsForCompanyUser(true);
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error fetching distributors list:", error);
      setIsError(true);
      setGenericModalProps(getGenericModalProps(`error`, `Error while fetching distributors list`));
      setIsLoading(false);
    }
  };

  // Check if user is a company or admin user
  const checkUserDesignation = () => {
    try {
      const currentDesignation = JSON.parse(localStorage.authContext).user.designation || [];
      const isCompany = Array.isArray(currentDesignation) 
        ? currentDesignation.includes("company") 
        : currentDesignation === "company";
      
      const reportadmin = Array.isArray(currentDesignation) 
        ? currentDesignation.includes("reportadmin") 
        : currentDesignation === "reportadmin";

      const isAdmin = Array.isArray(currentDesignation) 
        ? currentDesignation.includes("admin") 
        : currentDesignation === "admin";

      const isProjectadmin = Array.isArray(currentDesignation) 
        ? currentDesignation.includes("projectadmin") 
        : currentDesignation === "projectadmin";
      
      // Show distributor filter for both company and admin users
      const isCompanyOrAdmin = isCompany || isAdmin || reportadmin || isProjectadmin;
      setShowDistributorFilter(isCompanyOrAdmin);
      
      // Return whether this is a company/admin user to determine initialization flow
      return isCompanyOrAdmin;
    } catch (error) {
      console.error("Error checking user designation:", error);
      setShowDistributorFilter(false);
      return false;
    }
  };

  const getTableData = async (
    query: any = {},
    page?: number,
    limit?: number
  ) => {
    try {
      const response = await getDataFromLocalDb(
        "RealTimeStockData",
        query,
        page,
        limit
      );
      if (response.data.length > 0) {
        let skuWiseStockData: RealTimeStockRowsData[] = [];
        response.data.forEach((item: TransformedRealTimeStock) => {
          skuWiseStockData.push({
            id: item.skuCode,
            creationTime:
              item.caseQty?.lastModifiedTime ??
              item.pieceQty?.lastModifiedTime ??
              item.otherQty?.lastModifiedTime ??
              "",
            lastModifiedTime: [
              item.caseQty?.lastModifiedTime,
              item.pieceQty?.lastModifiedTime,
              item.otherQty?.lastModifiedTime
            ]
              .filter(Boolean)
              .sort((a, b) => dayjs(b).valueOf() - dayjs(a).valueOf())[0] || "",
            skuCode: item.skuCode,
            skuDescription: "",
            brand: "",
            category: "",
            shelfLife: 0,
            pieceCommittedQty: item.pieceQty?.committedQty ?? 0,
            pieceAvailableQty: item.pieceQty?.availableQty ?? 0,
            pieceUnsellableQty: item.pieceQty?.unsellableQty ?? 0,
            pieceInTransitQty: item.pieceQty?.inTransitQty ?? 0,
            pieceOnRouteQty: item.pieceQty?.onRouteQty ?? 0,
            pieceReturnedQty: item.pieceQty?.returnedQty ?? 0,
            caseCommittedQty: item.caseQty?.committedQty ?? 0,
            caseAvailableQty: item.caseQty?.availableQty ?? 0,
            caseUnsellableQty: item.caseQty?.unsellableQty ?? 0,
            caseInTransitQty: item.caseQty?.inTransitQty ?? 0,
            caseOnRouteQty: item.caseQty?.onRouteQty ?? 0,
            caseReturnedQty: item.caseQty?.returnedQty ?? 0,
            otherCommittedQty: item.otherQty?.committedQty ?? 0,
            otherAvailableQty: item.otherQty?.availableQty ?? 0,
            otherUnsellableQty: item.otherQty?.unsellableQty ?? 0,
            otherInTransitQty: item.otherQty?.inTransitQty ?? 0,
            otherOnRouteQty: item.otherQty?.onRouteQty ?? 0,
            otherReturnedQty: item.otherQty?.returnedQty ?? 0,
          });
        });
        const productsData = await getDataFromLocalDb("Product");
        const productsMap: Record<string, Product> =
          productsData?.data?.[0]?.data ?? {};
        const mergedData = getMergedCatalogueAndRealTimeStockData(
          skuWiseStockData,
          productsMap
        );
        setRows(mergedData);
        setData(mergedData);
        logAnalyticsEvent( "real_time_stocks_viewed", {
          "distributor_id": JSON.parse(localStorage.authContext).user.loginId,
          "total_skus_displayed,": mergedData.length
        });
      } else {
        setRows([]);
        setData([]);
        logAnalyticsEvent( "real_time_stocks_viewed", {
          "distributor_id": JSON.parse(localStorage.authContext).user.loginId,
          "total_skus_displayed,": 0
        });
      }
    } catch (error) {
      setRows([]);
      setData([]);
      logAnalyticsEvent( "real_time_stocks_viewed", {
        "distributor_id": JSON.parse(localStorage.authContext).user.loginId,
        "total_skus_displayed,": 0
      });
    }
  };

  const handleApplyFilter = () => {
    setFilterOpen(false);
  };

  const brandOptions = Array.from(new Set(data.map(row => row.brand).filter(Boolean))).map(brand => ({
    id: brand,
    label: brand,
    count: data.filter(row => row.brand === brand).length
  }));

  const categoryOptions = Array.from(new Set(data.map(row => row.category).filter(Boolean))).map(category => ({
    id: category,
    label: category,
    count: data.filter(row => row.category === category).length
  }));

  const filters = [
    {
      id: 'brand',
      label: 'Brand',
      options: brandOptions,
      selectedOptions: selectedBrands,
      setSelectedOptions: setSelectedBrands,
    },
    {
      id: 'category',
      label: 'Category',
      options: categoryOptions,
      selectedOptions: selectedCategories,
      setSelectedOptions: setSelectedCategories,
    }
  ];
  
  const init = async (distributorId?: string) => {
    setResetSearch(true); 
    setTimeout(() => setResetSearch(false), 0); 
    setSelectedBrands([]);
    setSelectedCategories([]);
    try {
      setIsLoading(true);
      
      await setBatchWiseStockDataInDb(distributorId);
      await setRealTimeStockDataInDb("", getQuantityType(), distributorId);
      setLastRefreshed(dayjs());
      //in case of company user?? what distributer??
      const stockSummaryTileData = await getStockSummaryTileData(distributorId);
      setTileData(stockSummaryTileData);
      await getTableData();
      const realTimeStocksReportIdConfig = await getConfigData(
        "dms_configuration",
        "realTimeStocksReportId"
      );
      realTimeStocksReportRef.current =
        realTimeStocksReportIdConfig[0]?.configValue ?? "";
    } catch (e) {
      console.error(e);
      setIsError(true);
      setGenericModalProps(getGenericModalProps(`error`, `Error while fetching real time stock data`));
    } finally {
      setIsLoading(false);
    }
  };
  const handleChildComponent = () => {
    return (
      <MultiCategoryFilter
        isOpen={filterOpen}
        setIsOpen={setFilterOpen}
        activeFilter={selectedFilterType}
        setActiveFilter={setSelectedFilterType}
        filters={filters}
        label="Brand & Category"
        searchValue={filterSearchValue}
        onSearchChange={setFilterSearchValue}
        showSearch={true}
      />
    );
  }

  useEffect(() => {
    const isCompanyOrAdmin = checkUserDesignation();
    
    if (isCompanyOrAdmin) {
      setIsLoading(true);
      fetchDistributors();
    } else {
      // For distributor users, set selectedDistributor to their own ID
      const currentUser = JSON.parse(localStorage.authContext).user;
      setSelectedDistributor(currentUser.loginId);
      logLoadTrace(init, "real_time_stock_page_load_time");
    }
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      init(selectedDistributor);
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [selectedDistributor]);

  return (
    <div className="RealTimeStock">
      <ScreenOverlay apply={isLoading} />
      <Header
        title={"Real-Time Stocks"}
        searchFunction={handleSearchValueChange}
        onRefresh={() => init(selectedDistributor)}
        downloadReportIcon={true}
        downloadReportOnClick={() =>{
            logAnalyticsEvent( "real_time_stocks_downloaded", {
              "distributor_id": JSON.parse(localStorage.authContext).user.loginId,
            });
            downloadAllReports(realTimeStocksReportRef.current, setSnackbarOpen,undefined, undefined, showDistributorFilter ? "loggedInUser" : undefined, selectedDistributor, selectedDistributor);
          }
        }
        lastRefreshed={lastRefreshed}
        resetSearch={resetSearch}
        showDistributorFilter={showDistributorFilter}
        onDistributorChange={handleDistributorChange}
        distributorList={distributors}
        enableTruncation={true}
        Component={!showDistributorFilter ? handleChildComponent() : undefined}
      />
      {isLoading ? (
        <Loader sx={{ position: "absolute", top: "45%" }} />
      ) : isError ? (
        <CustomGenericModal
          type={genericModalProps?.type}
          message={genericModalProps?.message}
          modalOpen={genericModalProps?.open}
          onClose={() => setGenericModalProps(initialGenericModalProps)}
        />
      ) : noDistributorsForCompanyUser ? (
        <>
          <EmptyPage
            height={87}
            text={
              <div style={{ textAlign: "center", whiteSpace: "pre-wrap" }}>
                No distributors exist for this company user.
                <br />
                Please navigate to the MDM page to add distributor details.
              </div>
            }
            imgSrc={realTimeEmptyImage}
            button={
              <button
                className="button-save-claim"
                style={{ height: "34px", width: "125px", marginTop: "10px" }}
                onClick={() => {
                  setConfirmPopupState(true);
                }}
              >
                <span className="button-save-text-claim">+ ADD</span>
              </button>
            }
          />
        </>
      ) : showDistributorFilter && !selectedDistributor ? (
        <>
          <EmptyPage
            height={87}
            text={
              <div style={{ textAlign: "center", whiteSpace: "pre-wrap" }}>
                Please select a distributor to view stock data.
              </div>
            }
            imgSrc={realTimeEmptyImage}
          />
        </>
      ) : rows.length === 0 && searchedValue.length === 0 && selectedBrands.length === 0 && selectedCategories.length === 0 ? (
        <>
          <EmptyPage
            height={87}
            text={
              <div style={{ textAlign: "center", whiteSpace: "pre-wrap" }}>
                {showDistributorFilter 
                  ? "No Stock Data Available for Selected Distributor." 
                  : "No Stock Data Available."}
                {!showDistributorFilter && (
                  <>
                    <br />
                    Click Below To Add Stock Data
                  </>
                )}
              </div>
            }
            imgSrc={realTimeEmptyImage}
            button={
              !showDistributorFilter && (
                <button
                  className="button-save-claim"
                  style={{ height: "34px", width: "125px", marginTop: "10px" }}
                  onClick={() => {
                    setConfirmPopupState(true);
                  }}
                >
                  <span className="button-save-text-claim">+ ADD</span>
                </button>
              )
            }
          />
        </>
      ) : (
        <>
          {!showDistributorFilter && handleChildComponent}
          <StockSummaryTile tileData={tileData} />
          <RealTimeStockTable
            rows={rows}
            columnsMetaData={
              realTimeStockColumnsMetadata.columns as ColumnMetaData<RealTimeStockRowsData>[]
            }
            filterParam={filterParam}
            stockTypeParam={stockTypeParam}
            defaultSelectedOption={stockTypeParam || "availableQty"}
          />
        </>
      )}
      <ConfirmationPopUp
        message={
          "You're about to be redirected to MDM page. Click Continue to proceed."
        }
        openConfirmModal={confirmPopupState}
        successMethod={() => {
          navigate("/dashboard/mdm2");
        }}
        setOpenConfirmModal={setConfirmPopupState}
      />
      <Snackbar
        open={snackbarOpen}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="info"
          variant="filled"
          sx={{ width: "100%" }}
        >
          Your report will be downloaded shortly.
        </Alert>
      </Snackbar>
    </div>
  );
};

export default RealTimeStock;
